import {MagnifyingGlassIcon, XMarkIcon} from "@heroicons/react/20/solid";
import {useMemo, useState} from "react";
import {AdminPage} from "../components/AdminPage";
import {
  otsInputClassName,
  otsInputValidBorderClassName,
} from "../components/OtsPasswordInput";
import {OtsBrand, OtsColorSchemePreference, OtsThemeRoot} from "../theme/OtsTheme";
import {DirectoryItem} from "./DirectoryItemCard";
import {DirectorySection} from "./DirectorySection";

export type DirectoryPageProps = {
  items: DirectoryItem[];
  utilityItems?: DirectoryItem[];
  groupLabels?: Record<string, string>;
  groupOrder?: string[];
  onNavigate?: (href: string) => void;
  title?: string;
  subtitle?: string;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
};

export const otsDirectoryGroupOrder = [
  "people",
  "companies",
  "assets",
  "locations",
  "operations",
  "routing",
  "pipelines",
  "production",
  "financial",
  "notifications",
  "master-data",
  "configuration",
  "security",
];

export const otsDirectoryGroupLabels: Record<string, string> = {
  people: "People",
  companies: "Companies",
  assets: "Assets",
  locations: "Locations",
  operations: "Operations",
  routing: "Routing",
  pipelines: "Pipelines",
  production: "Production",
  financial: "Financial",
  notifications: "Notifications",
  "master-data": "Master Data",
  configuration: "Configuration",
  security: "Security & Access",
};

const humanizeGroup = (group: string): string =>
  group
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const matches = (item: DirectoryItem, query: string): boolean => {
  if (!query) return true;
  return [item.title, item.description, ...(item.keywords ?? [])]
    .filter(Boolean)
    .join(" ")
    .toLocaleLowerCase()
    .includes(query);
};

/** Searchable, grouped navigation for operational records, setup, and app utilities. */
export const DirectoryPage = ({
  items,
  utilityItems = [],
  groupLabels = {},
  groupOrder = [],
  onNavigate,
  title = "Directory",
  subtitle = "Browse records, configuration, and tools available to you.",
  brand,
  colorScheme,
}: DirectoryPageProps) => {
  const [search, setSearch] = useState("");
  const query = search.trim().toLocaleLowerCase();

  const visibleUtilities = useMemo(
    () => utilityItems.filter((item) => matches(item, query)),
    [utilityItems, query]
  );

  const groups = useMemo(() => {
    const grouped = new Map<string, DirectoryItem[]>();
    items.filter((item) => matches(item, query)).forEach((item) => {
      const group = item.group ?? "other";
      grouped.set(group, [...(grouped.get(group) ?? []), item]);
    });

    const order = [...groupOrder, ...Array.from(grouped.keys()).filter((group) => !groupOrder.includes(group))];
    return order
      .filter((group, index) => order.indexOf(group) === index && grouped.has(group))
      .map((group) => ({
        id: group,
        title: groupLabels[group] ?? humanizeGroup(group),
        items: grouped.get(group) ?? [],
      }));
  }, [groupLabels, groupOrder, items, query]);

  const resultCount = visibleUtilities.length + groups.reduce((total, group) => total + group.items.length, 0);

  return (
    <OtsThemeRoot brand={brand} colorScheme={colorScheme} className="min-h-full">
      <AdminPage title={title} subtitle={subtitle}>
        <div className="mt-7 max-w-2xl">
          <label htmlFor="ots-directory-search" className="sr-only">Search directory</label>
          <div className="relative">
            <MagnifyingGlassIcon
              aria-hidden="true"
              className="pointer-events-none absolute top-1/2 left-3.5 size-5 -translate-y-1/2 text-[var(--ots-text-muted)]"/>
            <input
              id="ots-directory-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search the directory"
              className={[otsInputClassName, otsInputValidBorderClassName, "pr-11 pl-11"].join(" ")}/>
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                aria-label="Clear directory search"
                className="absolute inset-y-0 right-1.5 flex w-9 items-center justify-center rounded-md text-[var(--ots-text-muted)] hover:text-[var(--ots-primary)] focus-visible:outline-2 focus-visible:outline-[var(--ots-primary)]">
                <XMarkIcon aria-hidden="true" className="size-5"/>
              </button>
            )}
          </div>
          {query && (
            <p role="status" className="mt-2 text-xs text-[var(--ots-text-muted)]">
              {resultCount} {resultCount === 1 ? "result" : "results"}
            </p>
          )}
        </div>

        <div className="mt-9 space-y-10 pb-10">
          {visibleUtilities.length > 0 && (
            <DirectorySection
              title="Tools"
              description="Administrative workflows and local development controls."
              items={visibleUtilities}
              onNavigate={onNavigate}/>
          )}

          {groups.map((group) => (
            <DirectorySection
              key={group.id}
              title={group.title}
              items={group.items}
              onNavigate={onNavigate}/>
          ))}

          {resultCount === 0 && (
            <div className="rounded-xl border border-dashed border-[var(--ots-outline)] bg-[var(--ots-surface)] px-6 py-12 text-center">
              <p className="font-medium text-[var(--ots-text)]">No directory items match “{search.trim()}”.</p>
              <button
                type="button"
                onClick={() => setSearch("")}
                className="mt-3 text-sm font-semibold text-[var(--ots-primary)] hover:underline">
                Clear search
              </button>
            </div>
          )}
        </div>
      </AdminPage>
    </OtsThemeRoot>
  );
};
